/**
 * #709 owner-needed attention — a projection of canonical rows, never an authority.
 *
 * Plans stay the owner-decision store. This module only derives which of them currently
 * need Chin and whether a refresh added a genuinely new one (so the desk can sound once).
 */

import type { PlaybackPlan } from './notification-events';
import {
  effectiveEventMode,
  type NotificationSoundSettings,
} from './notification-sound-settings';
import { wasAnswered } from './plans';
import type { Plan } from './data';

/**
 * Stable keys of everything currently needing the owner.
 */
export function ownerNeededKeys(input: {
  readonly plans: readonly Plan[];
}): ReadonlySet<string> {
  const keys = new Set<string>();
  for (const plan of input.plans) {
    if (plan.status === 'proposed' || (plan.status === 'failed' && !wasAnswered(plan))) keys.add(`plan:${plan.id}`);
  }
  return keys;
}

/**
 * One refresh → at most one alert. The first snapshot (previous null) is silent, a key that
 * stays is silent, and a key that leaves and genuinely returns counts as new again.
 */
export function ownerNeededAdded(
  previous: ReadonlySet<string> | null,
  next: ReadonlySet<string>,
): readonly string[] {
  if (previous === null) return [];
  return [...next].filter((key) => !previous.has(key)).sort();
}

/** "Overseer needs your attention", attention chime as fallback; honours mute and settings. */
export function ownerNeededPlaybackPlan(settings: NotificationSoundSettings): PlaybackPlan {
  const eventMode = effectiveEventMode(settings, 'ownerNeeded');
  const behavior = settings.severity.needsAttention;
  const mode = behavior === 'off' ? 'off' : behavior === 'beep' && eventMode === 'voice' ? 'beep' : eventMode;
  const base = { severity: 'needsAttention' as const, critical: false, fallbackSfxId: 'sfx-attention' };
  if (mode === 'off') return { ...base, mode, tokenIds: [] };
  if (mode === 'beep') return { ...base, mode, tokenIds: ['sfx-attention'] };
  const lead = behavior === 'beep_and_voice' ? ['sfx-attention'] : [];
  return { ...base, mode, tokenIds: [...lead, 'token-overseer', 'phrase-needs-your-attention'] };
}

/** Owner-visible truth about where these alerts can reach. */
export const OWNER_NEEDED_DELIVERY_NOTE =
  'Alerts sound only while Command Center is open in a browser. There is no background or mobile push yet.';
